import api from './api';

export interface Company {
  id: number;
  name: string;
  slug: string;
  voen?: string;
  company_type?: string;
  status: string;
  phone?: string;
  email?: string;
  website?: string;
  address?: string;
  city?: string;
  district?: string;
  postal_code?: string;
  description?: string;
  capacity?: number;
  parent_company?: number;
  is_active: boolean;
  is_branch: boolean;
  full_name: string;
  member_count: number;
  student_count: number;
  created_at: string;
  updated_at: string;
}

export interface UserCompany {
  id: number;
  name: string;
  slug: string;
  company_type: string;
  status: string;
  city: string;
  full_name: string;
  is_branch: boolean;
  role: string;
  permissions: {
    can_manage_students: boolean;
    can_view_reports: boolean;
    can_manage_users: boolean;
  };
  student_count: number;
}

export interface CompanyFilters {
  search?: string;
  company_type?: string;
  status?: string;
  city?: string;
}

export interface CompanyStatistics {
  total_companies: number;
  active_companies: number;
  inactive_companies: number;
  by_company_type: Record<string, { label: string; count: number }>;
  by_status: Record<string, { label: string; count: number }>;
  by_city: Array<{ city: string; count: number }>;
}

export interface CompanyResponse {
  success: boolean;
  data?: UserCompany[];
  message?: string;
}

export interface CompanyListResponse {
  success: boolean;
  data?: Company[];
  message?: string;
}

export interface CompanyDetailResponse {
  success: boolean;
  data?: Company;
  message?: string;
  errors?: any;
}

export interface CompanyStatisticsResponse {
  success: boolean;
  data?: CompanyStatistics;
  message?: string;
}

export interface CitiesResponse {
  success: boolean;
  data?: string[];
  message?: string;
}

export const companyAPI = {
  getUserCompanies: async (): Promise<CompanyResponse> => {
    try {
      const response = await api.get('/auth/profile/');
      if (response.data.success && response.data.user?.companies) {
        return {
          success: true,
          data: response.data.user.companies
        };
      }
      return {
        success: false,
        message: 'Şirkət məlumatları tapılmadı'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Şirkətlər yüklənmədi'
      };
    }
  },

  getCompanies: async (filters?: CompanyFilters): Promise<CompanyListResponse> => {
    try {
      const params = new URLSearchParams();
      if (filters?.search) params.append('search', filters.search);
      if (filters?.company_type) params.append('company_type', filters.company_type);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.city) params.append('city', filters.city);

      const response = await api.get(`/companies/?${params.toString()}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Şirkətlər yüklənmədi'
      };
    }
  },

  createCompany: async (data: Partial<Company>): Promise<CompanyDetailResponse> => {
    try {
      const response = await api.post('/companies/', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Şirkət yaradılmadı',
        errors: error.response?.data?.errors
      };
    }
  },

  updateCompany: async (id: number, data: Partial<Company>): Promise<CompanyDetailResponse> => {
    try {
      const response = await api.put(`/companies/${id}/`, data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Şirkət yenilənmədi',
        errors: error.response?.data?.errors
      };
    }
  },

  deleteCompany: async (id: number): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await api.delete(`/companies/${id}/`);
      return {
        success: true,
        message: response.data.message || 'Şirkət silindi'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Şirkət silinmədi'
      };
    }
  },

  getStatistics: async (): Promise<CompanyStatisticsResponse> => {
    try {
      const response = await api.get('/companies/statistics/');
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Statistikalar yüklənmədi'
      };
    }
  },

  getCities: async (): Promise<CitiesResponse> => {
    try {
      const response = await api.get('/companies/cities/');
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Şəhərlər yüklənmədi'
      };
    }
  },
};